import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import './CompleteProfileModal.css';
import { useAuth } from '../contexts/AuthContext';

const CompleteProfileModal = ({ userId, pendingReferralCode, onSuccess, onClose }) => {
  const { user, authFetch } = useAuth();
  const [mobile, setMobile] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [autoUpdating, setAutoUpdating] = useState(false);

  // Pre-populate referral code if pendingReferralCode is provided.
  useEffect(() => {
    if (pendingReferralCode) {
      if (user && user.referral_code) {
        setReferralCode(user.referral_code);
        localStorage.removeItem('pending_referral_code');
      } else {
        setReferralCode(pendingReferralCode);
      }
    }
  }, [pendingReferralCode, user]);

  // Auto-populate mobile from localStorage pending_mobile (if exists and not already set).
  useEffect(() => {
    const pendingMobile = localStorage.getItem('pending_mobile');
    if (pendingMobile) {
      if (user && user.mobile_number) {
        setMobile(user.mobile_number);
        localStorage.removeItem('pending_mobile');
      } else {
        setMobile(pendingMobile);
      }
    }
  }, [user]);

  // Auto-update the profile in the background if both pending mobile and referral code exist.
  useEffect(() => {
    const pendingMobile = localStorage.getItem('pending_mobile');
    const pendingReferral = localStorage.getItem('pending_referral_code');
    // If both exist and user does not already have a mobile number set, auto-update.
    if (pendingMobile && pendingReferral && !(user && user.mobile_number)) {
      setAutoUpdating(true);
      const pendingName = localStorage.getItem('pending_name');
      const payload = {
        user_id: userId,
        mobile_number: pendingMobile,
        referral_code: pendingReferral.trim() || null
      };
      if (pendingName) {
        payload.name = decodeURIComponent(pendingName);
      }
      (async () => {
        try {
          const response = await authFetch(`${API_BASE_URL}/api/complete-profile`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const result = await response.json();
          if (result.success) {
            setShowSuccess(true);
            setTimeout(() => {
              onSuccess(pendingMobile);
            }, 2000);
          } else {
            setError(result.message || "Profile update failed automatically.");
          }
        } catch (err) {
          console.error(err);
          setError("Auto-update failed. Please complete manually.");
        } finally {
          setAutoUpdating(false);
          localStorage.removeItem('pending_mobile');
          localStorage.removeItem('pending_referral_code');
          localStorage.removeItem('pending_name');
        }
      })();
    }
  }, [user, userId, authFetch, onSuccess]);

  // Manual submit handler (if auto-update is not triggered).
  const handleSubmit = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setError("Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.");
      return;
    }
    setSubmitting(true);
    setError("");
    const pendingName = localStorage.getItem('pending_name');
    const payload = {
      user_id: userId,
      mobile_number: mobile,
      referral_code: referralCode.trim() || null
    };
    if (pendingName) {
      payload.name = decodeURIComponent(pendingName);
    }
    try {
      const response = await authFetch(`${API_BASE_URL}/api/complete-profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onSuccess(mobile);
        }, 2000);
      } else {
        setError(result.message || "Profile update failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancellation/skip of profile completion.
  // If the user doesn't already have a referral code, update it using the pending referral code.
  // Update the user session (localStorage) so that on refresh the referral code remains set.
  const handleSkip = async () => {
    if (user && !user.referral_code) {
      const pendingReferral = localStorage.getItem('pending_referral_code');
      if (pendingReferral) {
        try {
          const response = await authFetch(`${API_BASE_URL}/api/update-referral`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: user.user_id, referral_code: pendingReferral })
          });
          const result = await response.json();
          if (result.success) {
            console.log("Referral code updated in profile:", pendingReferral);
            const updatedUser = { ...user, referral_code: pendingReferral };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          } else {
            console.error("Failed to update referral code:", result.message);
          }
        } catch (error) {
          console.error("Error updating referral code:", error);
        }
        localStorage.removeItem('pending_referral_code');
      }
    }
    onClose();
  };

  // Display loader while auto-updating.
  if (autoUpdating) {
    return (
      <div className="modal-overlay">
        <div className="modal-box">
          <h2 className="mb-3 text-center">Updating your profile...</h2>
        </div>
      </div>
    );
  }

  // Render the manual complete profile form if auto-update is not active.
  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h2 className="mb-3 text-center">Complete Your Profile</h2>
        {showSuccess ? (
          <div className="success-message">🎉 Your profile was updated successfully!</div>
        ) : (
          <>
            <input
              type="tel"
              className="form-control mb-2"
              placeholder="Enter your mobile number (e.g. 9876543210)"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Got a referral code? (optional)"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              readOnly={Boolean(pendingReferralCode || (user && user.referral_code))}
            />
            {error && <div className="error-message">{error}</div>}
            <div className="button-group">
              <button className="btn btn-secondary" onClick={handleSkip}>
                Skip for Now
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Saving..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompleteProfileModal;
